import React, { useEffect, useState, useCallback } from 'react'
import styled from 'styled-components'

import { Button } from '@nexiahome/ui-common/components/primitives/buttons'
import Card from 'components/Card'
import Checkbox from 'components/Checkbox'
import Spinner from 'components/Spinner'

import Brand from 'helpers/brand'
import GA4 from 'helpers/ga4'

import useSearchParams from 'hooks/useSearchParams'
import { useSessionContext } from 'hooks/SessionContext'
import useNotificationRecipient from 'hooks/useNotificationRecipient'

import Api from 'services/api'

import { color } from 'styles/colors'
import { ButtonContainer, NoData, PageHeader, PageTitle, PanelTitle, screenSize } from 'styles/shared'

const EmailSubscriptionPreferences = () => {
  const alertCode = useSearchParams('alertCode')
  const deviceId = useSearchParams('deviceId')
  const customerName = useSearchParams('customerName')
  const customerId = parseInt(useSearchParams('customerId'))
  const notificationRecipientId = parseInt(useSearchParams('recipientId'))

  const { session: { dealerId } } = useSessionContext()
  const { notificationRecipient, isNotificationRecipientLoaded } = useNotificationRecipient(dealerId, notificationRecipientId)

  const [isMuteAlertChecked, setIsMuteAlertChecked] = useState(false)
  const [isMuteCustomerChecked, setIsMuteCustomerChecked] = useState(false)
  const [isMuteAlertForDeviceChecked, setIsMuteAlertForDeviceChecked] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [arePreferencesUpdated, setArePreferencesUpdated] = useState(false)
  const [hasUserMadeMuteAlertChange, setHasUserMadeMuteAlertChange] = useState(false)
  const [hasUserMadeMuteCustomerChange, setHasUserMadeMuteCustomerChange] = useState(false)
  const [hasUserMadeMuteDeviceChange, setHasUserMadeMuteDeviceChange] = useState(false)

  useEffect(() => {
    if (isNotificationRecipientLoaded) {
      setIsMuteAlertChecked(!!notificationRecipient?.mutedAlertCodes?.find((mutedAlertCode) => mutedAlertCode.code === alertCode))
      setIsMuteCustomerChecked(!!notificationRecipient?.mutedCustomerIds?.find((mutedCustomer) => mutedCustomer.id === customerId))
      setIsMuteAlertForDeviceChecked(!!notificationRecipient?.mutedDeviceIdsForAlertCodes
        ?.find((mutedDeviceIdForAlertCode) => mutedDeviceIdForAlertCode.code === alertCode && mutedDeviceIdForAlertCode.deviceId === deviceId))
    }
  }, [isNotificationRecipientLoaded]) // eslint-disable-line react-hooks/exhaustive-deps

  const onClickUpdate = useCallback(() => {
    setIsUpdating(true)
    Api.MutedNotifications.update({
      notification_recipient_id: notificationRecipientId,
      alert_code: alertCode,
      customer_id: customerId,
      device_id: deviceId,
      mute_notifications_for_customer: isMuteCustomerChecked,
      mute_notifications_for_alert: isMuteAlertChecked,
      mute_notifications_for_device_by_alerts: isMuteAlertForDeviceChecked
    })
      .then(() => {
        setArePreferencesUpdated(true)
        setArePreferencesUpdated(true)

        hasUserMadeMuteAlertChange && sendEvent('Alert', isMuteAlertChecked, alertCode)
        hasUserMadeMuteCustomerChange && sendEvent('Customer', isMuteCustomerChecked, customerId)
        hasUserMadeMuteDeviceChange && sendEvent('Device Alert', isMuteAlertForDeviceChecked, deviceId)
      })
      .finally(() => {
        setIsUpdating(false)
      })
  }, [notificationRecipientId, alertCode, customerId, deviceId, isMuteCustomerChecked, isMuteAlertChecked, isMuteAlertForDeviceChecked, hasUserMadeMuteAlertChange, hasUserMadeMuteCustomerChange, hasUserMadeMuteDeviceChange])

  const sendEvent = (field, muted, value) => {
    GA4.sendEvent({ category: 'Email Subscription Preferences', action: `${field} preference updated`, label: muted ? 'Muted' : 'Unmuted', value })
  }

  if (!isNotificationRecipientLoaded) {
    return <Spinner variant="page" />
  }

  return (
    <S.Body>
      <PageHeader>
        <S.ProductLogo src={Brand.productLogoPath()} alt={`${Brand.brandName()} logo`} />
        <PageTitle>Email Subscription Preferences</PageTitle>
      </PageHeader>

      <S.Card>
        <S.BrandLogo src={Brand.brandLogoPath()} alt={`${Brand.productName()} logo`} />

        <S.Settings>
          {arePreferencesUpdated && (
            <NoData>
              <h1>Success</h1>
              <h2>Your preferences have been updated</h2>
            </NoData>
          )}

          {!arePreferencesUpdated && (
            <>
              <PanelTitle>Modify Your Email Subscription Preferences</PanelTitle>

              <S.Checkboxes>
                <S.Checkbox
                  label={`Mute Alert (${alertCode || ''}) for All Customers`}
                  checked={isMuteAlertChecked}
                  onChange={() => {
                    setHasUserMadeMuteAlertChange(!hasUserMadeMuteAlertChange)
                    setIsMuteAlertChecked(!isMuteAlertChecked)
                  }}
                />
                <S.HelperText>Mute emails for this specific alert for all customers.</S.HelperText>

                <S.Checkbox
                  label={`Mute All Alerts for Customer (${customerName || ''})`}
                  checked={isMuteCustomerChecked}
                  onChange={() => {
                    setHasUserMadeMuteCustomerChange(!hasUserMadeMuteCustomerChange)
                    setIsMuteCustomerChecked(!isMuteCustomerChecked)
                  }}
                />
                <S.HelperText>Mute all alert emails for this specific customer. </S.HelperText>

                <S.Checkbox
                  label={`Mute this Alert (${alertCode || ''}) for this Device (${deviceId || ''})`}
                  checked={isMuteAlertForDeviceChecked}
                  onChange={() => {
                    setHasUserMadeMuteDeviceChange(!hasUserMadeMuteDeviceChange)
                    setIsMuteAlertForDeviceChecked(!isMuteAlertForDeviceChecked)
                  }}
                />
                <S.HelperText>Mute emails for this specific alert for this Device </S.HelperText>

              </S.Checkboxes>

              <PanelTitle>Your Email Address</PanelTitle>
              <b>{notificationRecipient?.email}</b>

              <S.Note>NOTE: You can always undo these changes from the Notifications page in {Brand.productName()}</S.Note>

              <ButtonContainer>
                <Button
                  decoration="positive"
                  enabled={!isUpdating && (hasUserMadeMuteAlertChange || hasUserMadeMuteCustomerChange || hasUserMadeMuteDeviceChange)}
                  text="Update"
                  onPress={onClickUpdate}
                />
              </ButtonContainer>
            </>
          )}
        </S.Settings>
      </S.Card>
    </S.Body>
  )
}

export default EmailSubscriptionPreferences

const S = {
  Body: styled.div`
    height: 100%;
    background-color: ${color('background')};
  `,
  Card: styled(Card)`
    margin: 10px;
    position: relative;
  `,
  ProductLogo: styled.img`
    height: 36px;
    position: absolute;
    top: 15px;
    right: 15px;
  `,
  Settings: styled.div`
    max-width: 600px;
    margin: 0 auto;

    @media ${screenSize.medium} {
      margin: unset;
    }
  `,
  Checkboxes: styled.div`
    padding: 10px 0 30px;
  `,
  Checkbox: styled(Checkbox)`
    > label {
      font-weight: bold;
    }
  `,
  HelperText: styled.p`
    margin-left: 30px;
  `,
  BrandLogo: styled.img`
    height: 34px;
    position: absolute;
    left: 15px;
    bottom: 20px;
  `,
  Note: styled.p`
    margin-top: 30px;
  `
}
